/**
 * หน้าจอความล้มเหลวของ BOM/Routing — หนึ่งสถานะ หนึ่งข้อความ
 *
 * ทุกกรณีที่ `docs/API_CONTRACT.md` นิยามไว้ต้องอ่านออกว่าเกิดอะไรและต้องทำอะไรต่อ
 * **ห้ามยุบเป็น "บันทึกไม่สำเร็จ" ก้อนเดียว** — ผู้ใช้ที่ไม่รู้ว่าชนกฎข้อไหนจะแก้
 * อะไรไม่ถูก แล้วสรุปว่าระบบเสีย
 *
 * กฎทั้งสี่ที่ contract เขียนไว้ตรง ๆ (`PRODUCT_NOT_ACTIVE`,
 * `EFFECTIVE_DATE_NOT_AFTER_CURRENT`, `COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF`,
 * `WORK_CENTER_NOT_IN_PLANT`) มีข้อความของตัวเองคนละข้อความ และไม่มีข้อไหนอ่าน
 * เหมือนอีกข้อ เพราะสี่กรณีนี้ต้องแก้คนละอย่างกันหมด
 *
 * `403` ของกลุ่มนี้ **ไม่ใช่** "ทำรายการไม่สำเร็จ" แต่คือ "โรงงานนี้อยู่นอกขอบเขต
 * ของคุณ" (D-004) ซึ่งเป็นคำตอบที่ผู้ใช้ทำอะไรต่อได้จริง
 */

import { StatusBadge } from '../StatusBadge';
import type { ApiErrorEnvelope, FormulaFailure } from '../../lib/bom/client';
import {
  COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF,
  EFFECTIVE_DATE_NOT_AFTER_CURRENT,
  PRODUCT_NOT_ACTIVE,
  WORK_CENTER_NOT_IN_PLANT,
} from '../../lib/bom/types';

export type FormulaFailureContext =
  /** โหลดตัวสินค้าที่หน้าจอนี้กำลังพูดถึง */
  | 'product'
  /** อ่านสูตรที่ใช้อยู่ หรืออ่านประวัติ revision */
  | 'read'
  /** เขียน revision ใหม่ */
  | 'create';

export interface BomFailureNoticeProps {
  readonly failure: FormulaFailure;
  readonly context: FormulaFailureContext;
  /** โรงงานที่คำขอนี้อ้างถึง — `403`/`404` อ่านรู้เรื่องขึ้นมากเมื่อบอกว่าโรงงานไหน */
  readonly plantCode?: string;
  readonly onRetry?: () => void;
}

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function envelopeOf(failure: FormulaFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

/**
 * ข้อความของกฎทางธุรกิจทั้งสี่ — คืน `null` เมื่อไม่ใช่กฎที่ contract เขียนไว้
 *
 * แยกออกมาจาก `failureCopy()` เพราะกฎเหล่านี้มาพร้อม `422` เสมอ แต่ **ความหมายของ
 * มันไม่ใช่ "422"** มันคือคำอธิบายว่าต้องแก้อะไร และนั่นคือสิ่งเดียวที่ผู้ใช้ต้องอ่าน
 */
function ruleCopy(rule: string | null): FailureCopy | null {
  switch (rule) {
    case PRODUCT_NOT_ACTIVE:
      return {
        heading: 'สินค้าตัวนี้ยังไม่ได้ใช้งานจริง จึงยังเขียนสูตรให้ไม่ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${PRODUCT_NOT_ACTIVE} — สร้าง BOM/Routing ได้เฉพาะสินค้าที่สถานะเป็น ACTIVE แล้ว รายการที่ยังเป็นร่างหรือรออนุมัติยังเปลี่ยนได้ตลอด สูตรที่ผูกกับมันจึงยังไม่มีความหมาย ให้เดินเส้นทางอนุมัติสินค้าให้จบก่อน แล้วกลับมาที่หน้านี้อีกครั้ง`,
      };
    case EFFECTIVE_DATE_NOT_AFTER_CURRENT:
      return {
        heading: 'วันที่มีผลต้องอยู่หลังวันที่ฉบับปัจจุบันเริ่มมีผล',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${EFFECTIVE_DATE_NOT_AFTER_CURRENT} — revision ใหม่ปิดฉบับเดิมด้วยวันที่นี้ ถ้าวันที่ไม่ได้อยู่ถัดจากวันเริ่มมีผลของฉบับปัจจุบัน ช่วงเวลาสองฉบับจะทับกันจนบอกไม่ได้ว่าวันนั้นโรงงานผลิตตามสูตรไหน แก้วันที่มีผลให้หลังกว่านั้นแล้วส่งใหม่ ไม่มีอะไรถูกบันทึกไว้`,
      };
    case COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF:
      return {
        heading: 'มีชิ้นส่วนที่อ้างถึงสินค้าตัวเอง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF} — สูตรที่ใส่ตัวเองเป็นชิ้นส่วนของตัวเองคือวงวนที่การกระจายความต้องการวัสดุจะวนไม่รู้จบ แก้รหัสชิ้นส่วนแถวที่อ้างถึงสินค้าตัวนี้ให้เป็นชิ้นส่วนจริง แล้วส่งใหม่ทั้งชุด`,
      };
    case WORK_CENTER_NOT_IN_PLANT:
      return {
        heading: 'หน่วยผลิตที่ระบุไม่ได้อยู่ในโรงงานนี้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${WORK_CENTER_NOT_IN_PLANT} — Routing เป็นของโรงงานใดโรงงานหนึ่งเสมอ และขั้นตอนหนึ่งจะสั่งงานเครื่องที่อยู่อีกโรงงานไม่ได้ ตรวจว่าเลือกโรงงานถูกฉบับ หรือแก้รหัสหน่วยผลิตให้เป็นเครื่องที่อยู่ในโรงงานนี้`,
      };
    default:
      return null;
  }
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(
  failure: FormulaFailure,
  context: FormulaFailureContext,
  plantCode: string | undefined,
): FailureCopy {
  const plant = plantCode === undefined || plantCode === '' ? 'โรงงานที่เลือกอยู่' : plantCode;

  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      // 403 ของกลุ่มนี้แปลว่า "อยู่นอกขอบเขตโรงงานของคุณ" เป็นอันดับแรก เพราะ
      // `plant_code` บังคับเสมอและเป็นขอบเขตสิทธิ์ (D-004) — contract ระบุด้วยว่า
      // Plant นอก scope ตอบ `403` ไม่ใช่ `404` โดยตั้งใจ
      return {
        heading: `โรงงาน ${plant} อยู่นอกขอบเขตที่คุณเข้าถึงได้`,
        text:
          context === 'create'
            ? `เซิร์ฟเวอร์ตอบ 403 — บัญชีนี้เขียนสูตรของโรงงานนี้ไม่ได้ BOM และ Routing เป็นของโรงงานใดโรงงานหนึ่งเสมอ (D-004) ยังไม่มี revision ใหม่ถูกบันทึก และฉบับที่ใช้อยู่ยังเป็นฉบับเดิม`
            : `เซิร์ฟเวอร์ตอบ 403 — บัญชีนี้ดูสูตรของโรงงานนี้ไม่ได้ BOM และ Routing เป็นของโรงงานใดโรงงานหนึ่งเสมอ (D-004) ลองเลือกโรงงานอื่นที่คุณมีสิทธิ์ หรือขอสิทธิ์โรงงานนี้กับผู้ดูแลระบบ`,
      };
    case 'not-found':
      return {
        heading:
          context === 'product'
            ? 'ไม่พบสินค้าที่หน้าจอนี้อ้างถึง'
            : 'ไม่พบสินค้านี้ในมุมมองของคุณ',
        text: 'เซิร์ฟเวอร์ตอบ 404 — ระบบไม่ยืนยันว่ารหัสนี้ไม่มีอยู่จริง หรือมีอยู่แต่อยู่นอกโรงงานที่คุณมีสิทธิ์เห็น คนละเรื่องกับ “สินค้านี้มีอยู่แต่ยังไม่ได้เขียนสูตร” ซึ่งเซิร์ฟเวอร์ตอบ 200 และหน้าจอนี้แสดงเป็นสถานะว่างของมันเอง',
      };
    case 'conflict':
      return {
        heading: 'คำขอนี้ชนกับสถานะปัจจุบันของข้อมูล',
        text: 'เซิร์ฟเวอร์ตอบ 409 — ไม่มี revision ใหม่ถูกบันทึก ดูข้อความจากเซิร์ฟเวอร์ด้านล่างประกอบ แล้วโหลดหน้านี้ใหม่เพื่อดูว่าฉบับที่ใช้อยู่ตอนนี้เป็นฉบับไหน',
      };
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule) ?? {
          heading: 'เขียน revision ใหม่ไม่ได้เพราะติดกฎทางธุรกิจ',
          text: 'เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน — ไม่ใช่ข้อผิดพลาดของระบบ แต่เป็นเงื่อนไขที่ต้องแก้ก่อน และยังไม่มีอะไรถูกบันทึก',
        }
      );
    case 'bad-request':
      return {
        heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
        text:
          context === 'create'
            ? 'เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ (เช่น ลำดับรายการซ้ำกัน หรือส่งชุดรายการว่าง) ยังไม่มี revision ใหม่ถูกบันทึก'
            : 'เซิร์ฟเวอร์ตอบ 400 — คำขอที่ส่งไปไม่ตรงรูปแบบที่ API รับ',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text:
          context === 'create'
            ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ไม่มีอะไรยืนยันว่า revision ใหม่ถูกบันทึก — ให้ถือว่าสูตรยังเป็นฉบับเดิมจนกว่าจะโหลดใหม่แล้วเห็นด้วยตา'
            : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีข้อมูลจริงมาแสดง',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text:
          context === 'create'
            ? `${failure.detail} — คำขอไปไม่ถึงเซิร์ฟเวอร์ revision ใหม่จึงยังไม่ถูกบันทึก และหน้าจอนี้จะไม่ส่งซ้ำให้เองภายหลัง`
            : `${failure.detail} — หน้าจอนี้จึงไม่มีสูตรจะแสดง และจะไม่เดาสูตรขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงสูตรที่อ่านได้ไม่ครบ ดีกว่าแสดงรายการชิ้นส่วนที่ไม่รู้ว่าถูกหรือผิด แล้วมีคนผลิตตาม',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

export function BomFailureNotice({
  failure,
  context,
  plantCode,
  onRetry,
}: BomFailureNoticeProps) {
  const copy = failureCopy(failure, context, plantCode);
  const envelope = envelopeOf(failure);
  const rule = envelope === null ? null : envelope.rule;
  const serverMessage = envelope === null ? null : envelope.message;

  return (
    <div
      className="cap-notice"
      data-testid="formula-failure"
      data-failure={failure.status}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={failure.status === 'rule-violation' ? 'warning' : 'danger'}>
        {failure.status === 'rule-violation' ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน:{' '}
          <code className="cap-table__code" data-testid="formula-rule">
            {rule}
          </code>
        </p>
      )}

      {serverMessage === null ? null : (
        <p className="cap-notice__note" data-testid="formula-failure-message">
          ข้อความจากเซิร์ฟเวอร์: {serverMessage}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
